import type { Campaign, CampaignFilters, CampaignStats, CampaignTypeInfo, CampaignStatusInfo, SubmissionStats } from '../types'
import type { APIResponse } from '@/shared/types'
import { apiService, API_BASE_URL } from '@/shared/services/api'
import axios from 'axios'
import Cookies from 'js-cookie'

export class CampaignService {
  /**
   * Get all available campaign types
   */
  static async getCampaignTypes(): Promise<APIResponse<CampaignTypeInfo[]>> {
    try {
      const response = await apiService.get('/campaigns/types')
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaign types:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign types')
    }
  }

  /**
   * Get all available campaign statuses
   */
  static async getCampaignStatuses(): Promise<APIResponse<CampaignStatusInfo[]>> {
    try {
      const response = await apiService.get('/campaigns/statuses')
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaign statuses:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign statuses')
    }
  }

  /**
   * Get all campaigns with optional filtering
   */
  static async getCampaigns(filters?: CampaignFilters): Promise<APIResponse<Campaign[]>> {
    try {
      const params = new URLSearchParams()
      
      if (filters?.search) params.append('search', filters.search)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.organizationId) params.append('organizationId', filters.organizationId)
      if (filters?.ownerId) params.append('ownerId', filters.ownerId)
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())

      const response = await apiService.get(`/campaigns?${params.toString()}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaigns:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaigns')
    }
  }

  /**
   * Get a single campaign by ID
   */
  static async getCampaignById(id: string): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.get(`/campaigns/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign')
    }
  }

  /**
   * Create a new campaign
   */
  static async createCampaign(campaignData: Partial<Campaign>): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.post('/campaigns', campaignData)
      return response.data
    } catch (error: any) {
      console.error('Error creating campaign:', error)
      // Preserve validation details if they exist
      if (error.response?.data?.details) {
        const validationError: any = new Error(error.response?.data?.error || 'Validation failed')
        validationError.details = error.response.data.details
        throw validationError
      }
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Failed to create campaign')
    }
  }

  /**
   * Update an existing campaign
   */
  static async updateCampaign(id: string, campaignData: Partial<Campaign>): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.put(`/campaigns/${id}`, campaignData)
      return response.data
    } catch (error: any) {
      console.error('Error updating campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to update campaign')
    }
  }

  /**
   * Delete a campaign
   */
  static async deleteCampaign(id: string): Promise<APIResponse<void>> {
    try {
      const response = await apiService.delete(`/campaigns/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error deleting campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to delete campaign')
    }
  }

  /**
   * Update campaign status
   */
  static async updateCampaignStatus(
    id: string, 
    status: "ACTIVE" | "INACTIVE" | "DISABLED" | "HIDDEN"
  ): Promise<APIResponse<Campaign>> {
    try {
      const token = Cookies.get('token')
      const response = await axios.patch(`${API_BASE_URL}/campaigns/${id}/status`, 
        { status },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          withCredentials: true
        }
      )
      return response.data
    } catch (error: any) {
      console.error('Error updating campaign status:', error)
      throw new Error(error.response?.data?.message || 'Failed to update campaign status')
    }
  }

  /**
   * Get campaign statistics
   */
  static async getCampaignStats(organizationId?: string): Promise<APIResponse<CampaignStats>> {
    try {
      const params = organizationId ? `?organizationId=${organizationId}` : ''
      const response = await apiService.get(`/campaigns/stats${params}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaign stats:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign statistics')
    }
  }

  /**
   * Get submission statistics
   */
  static async getSubmissionStats(organizationId?: string, timeFilter?: string, page?: number, limit?: number): Promise<APIResponse<SubmissionStats>> {
    try {
      const params = new URLSearchParams()
      if (organizationId) params.append('organizationId', organizationId)
      if (timeFilter) params.append('timeFilter', timeFilter)
      if (page) params.append('page', page.toString())
      if (limit) params.append('limit', limit.toString())
      
      const queryString = params.toString()
      const response = await apiService.get(`/campaigns/submission-stats${queryString ? `?${queryString}` : ''}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching submission stats:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch submission statistics')
    }
  }

  /**
   * Get campaigns for a specific organization
   */
  static async getCampaignsByOrganization(organizationId: string): Promise<APIResponse<Campaign[]>> {
    try {
      const response = await apiService.get(`/campaigns/organization/${organizationId}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching organization campaigns:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch organization campaigns')
    }
  }

  /**
   * Get campaigns owned by a specific user
   */
  static async getCampaignsByOwner(ownerId: string): Promise<APIResponse<Campaign[]>> {
    try {
      const response = await apiService.get(`/users/${ownerId}/campaigns`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching user campaigns:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch user campaigns')
    }
  }

  /**
   * Duplicate a campaign
   */
  static async duplicateCampaign(id: string, newName: string): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.post(`/campaigns/${id}/duplicate`, { name: newName })
      return response.data
    } catch (error: any) {
      console.error('Error duplicating campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to duplicate campaign')
    }
  }

  /**
   * Archive a campaign (soft delete)
   */
  static async archiveCampaign(id: string): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.put(`/campaigns/${id}/archive`)
      return response.data
    } catch (error: any) {
      console.error('Error archiving campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to archive campaign')
    }
  }

  /**
   * Restore an archived campaign
   */
  static async restoreCampaign(id: string): Promise<APIResponse<Campaign>> {
    try {
      const response = await apiService.put(`/campaigns/${id}/restore`)
      return response.data
    } catch (error: any) {
      console.error('Error restoring campaign:', error)
      throw new Error(error.response?.data?.message || 'Failed to restore campaign')
    }
  }

  /**
   * Assign campaign to organization
   */
  static async assignCampaignToOrganization(
    campaignId: string, 
    organizationId: string,
    inheritToChildren: boolean = false
  ): Promise<APIResponse<any>> {
    try {
      const response = await apiService.post(`/campaigns/${campaignId}/assign-organization`, {
        organizationId,
        inheritToChildren
      })
      return response.data
    } catch (error: any) {
      console.error('Error assigning campaign to organization:', error)
      throw new Error(error.response?.data?.message || 'Failed to assign campaign to organization')
    }
  }

  /**
   * Bulk assign campaign to organizations
   */
  static async bulkAssignCampaignToOrganizations(
    campaignId: string, 
    organizationIds: string[]
  ): Promise<APIResponse<any>> {
    try {
      const response = await apiService.post(`/campaigns/${campaignId}/bulk-assign-organizations`, {
        organizationIds
      })
      return response.data
    } catch (error: any) {
      console.error('Error bulk assigning campaign to organizations:', error)
      throw new Error(error.response?.data?.message || 'Failed to bulk assign campaign to organizations')
    }
  }

  /**
   * Remove campaign from organization
   */
  static async removeCampaignFromOrganization(
    campaignId: string, 
    organizationId: string
  ): Promise<APIResponse<any>> {
    try {
      const response = await apiService.delete(`/campaigns/${campaignId}/remove-organization`, {
        data: { organizationId }
      })
      return response.data
    } catch (error: any) {
      console.error('Error removing campaign from organization:', error)
      throw new Error(error.response?.data?.message || 'Failed to remove campaign from organization')
    }
  }

  /**
   * Get campaign organization assignments
   */
  static async getCampaignOrganizationAssignments(campaignId: string): Promise<APIResponse<any[]>> {
    try {
      const response = await apiService.get(`/campaigns/${campaignId}/organization-assignments`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching campaign organization assignments:', error)
      throw new Error(error.response?.data?.message || 'Failed to fetch campaign organization assignments')
    }
  }

}export default CampaignService


